import { useState } from 'react';
import * as tus from 'tus-js-client';
import * as Base64 from 'js-base64';

import { useCreateUploadVideoUrlCommand } from 'legacy/events/hooks';
import { CreateUploadVideoUrlResponseDto } from 'legacy/events/types';

const UPLOAD_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
/**
 *
 * @param file
 * @returns
 */
const tusUploadOptions = (file: File) => ({
  chunkSize: UPLOAD_CHUNK_SIZE,
  metadata: {
    filename: file.name,
    filetype: file.type
  }
});

const tusUpload = async (
  file: File,
  uploadUrl: string,
  options: {
    onProgress?: ((bytesSent: number, bytesTotal: number) => void) | null;
  }
) =>
  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      ...tusUploadOptions(file),
      uploadUrl,
      onSuccess: () => {
        resolve();
      },
      onError: reject,
      onProgress: options.onProgress
    });
    upload.start();
  });

export const useVideoUploadCommand = () => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const createUploadVideoUrl = useCreateUploadVideoUrlCommand();

  const onProgress = (sent: number, total: number) => {
    setProgress((sent / total) * 100);
  };

  const useVideoUpload = async (file: File): Promise<string> => {
    setIsUploading(true);
    try {
      const { uploadUrl, videoId } =
        await new Promise<CreateUploadVideoUrlResponseDto>(
          (resolve, reject) => {
            createUploadVideoUrl.mutate(
              {
                uploadLength: String(file.size),
                uploadMetadata: `filename=${Base64.encode(
                  String(file.name)
                )};filetype=${Base64.encode(String(file.type))}`
              },
              {
                onSuccess: resolve,
                onError: reject
              }
            );
          }
        );
      await tusUpload(file, uploadUrl, { onProgress });
      return videoId;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    progress,
    isUploading,
    useVideoUpload
  };
};
